import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { ThemeProvider } from "@mui/material/styles";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import {
  attendanceAdminDailyApi,
  attendanceAdminSummaryApi,
} from "../../apiCalls/Apicalls";
import { theme } from "../../utilities/dataGridHelpers";
import AttendanceWidget from "./AttendanceWidget";

const PERIOD_TABS = [
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
  { label: "Quarterly", value: "quarterly" },
  { label: "Yearly", value: "yearly" },
];

const STATUS_STYLE = {
  present: { bg: "rgba(34,197,94,0.1)", color: "#16a34a" },
  late: { bg: "rgba(251,146,60,0.1)", color: "#ea580c" },
  "half-day": { bg: "rgba(168,85,247,0.1)", color: "#9333ea" },
  absent: { bg: "rgba(239,68,68,0.1)", color: "#dc2626" },
};

function StatusChip({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.absent;
  const label = status
    ? status.charAt(0).toUpperCase() + status.slice(1).replace("-", " ")
    : "Absent";
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1,
        py: 0.25,
        bgcolor: s.bg,
        color: s.color,
        borderRadius: "5px",
        fontSize: "0.74rem",
        fontWeight: 600,
        lineHeight: 1.6,
      }}
    >
      {label}
    </Box>
  );
}

function formatTime(isoString) {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function AttPct({ value }) {
  const pct = value ?? 0;
  const color =
    pct >= 90 ? "#16a34a" : pct >= 75 ? "#ea580c" : "#dc2626";
  return (
    <Stack direction="row" alignItems="center" spacing={1} width="100%">
      <Box sx={{ flex: 1 }}>
        <LinearProgress
          variant="determinate"
          value={Math.min(pct, 100)}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: "var(--ax-border)",
            "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 3 },
          }}
        />
      </Box>
      <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color, minWidth: 40 }}>
        {pct}%
      </Typography>
    </Stack>
  );
}

function CoordCell({ value }) {
  if (!value || value.latitude == null) {
    return <Typography sx={{ fontSize: "0.78rem", color: "var(--ax-ink-2)" }}>—</Typography>;
  }
  const coords = `${value.latitude.toFixed(5)}, ${value.longitude.toFixed(5)}`;
  const accuracy = value.accuracy ? ` ±${Math.round(value.accuracy)}m` : "";
  const mapsUrl = `https://www.google.com/maps?q=${value.latitude},${value.longitude}`;
  return (
    <Tooltip title={`${coords}${accuracy}`} arrow>
      <Typography
        component="a"
        href={mapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        sx={{
          fontSize: "0.78rem",
          color: "var(--ax-blue)",
          textDecoration: "none",
          "&:hover": { textDecoration: "underline" },
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          display: "block",
          maxWidth: 150,
        }}
      >
        {coords}
      </Typography>
    </Tooltip>
  );
}

// ── Daily columns ─────────────────────────────────────────────────────────────
const dailyColumns = [
  { field: "empId", headerName: "Emp ID", width: 100 },
  { field: "empName", headerName: "Name", flex: 1, minWidth: 150 },
  { field: "department", headerName: "Department", width: 130 },
  {
    field: "status",
    headerName: "Status",
    width: 105,
    renderCell: (p) => <StatusChip status={p.value} />,
  },
  {
    field: "checkInTime",
    headerName: "Check In",
    width: 105,
    renderCell: (p) => (
      <Typography sx={{ fontSize: "0.82rem" }}>{formatTime(p.value)}</Typography>
    ),
  },
  {
    field: "checkInLocation",
    headerName: "In Location",
    width: 170,
    renderCell: (p) => <CoordCell value={p.value} />,
  },
  {
    field: "checkOutTime",
    headerName: "Check Out",
    width: 105,
    renderCell: (p) => (
      <Typography sx={{ fontSize: "0.82rem" }}>{formatTime(p.value)}</Typography>
    ),
  },
  {
    field: "checkOutLocation",
    headerName: "Out Location",
    width: 170,
    renderCell: (p) => <CoordCell value={p.value} />,
  },
  {
    field: "workingHours",
    headerName: "Hours",
    width: 80,
    renderCell: (p) => (
      <Typography sx={{ fontSize: "0.82rem" }}>
        {p.value != null ? `${p.value.toFixed(1)}h` : "—"}
      </Typography>
    ),
  },
];

// ── Summary columns (weekly / monthly / quarterly / yearly) ───────────────────
const summaryColumns = [
  { field: "empId", headerName: "Emp ID", width: 100 },
  { field: "empName", headerName: "Name", flex: 1, minWidth: 160 },
  { field: "department", headerName: "Department", width: 140 },
  { field: "workingDays", headerName: "Working Days", width: 120, type: "number" },
  { field: "attendedDays", headerName: "Attended", width: 100, type: "number" },
  { field: "presentDays", headerName: "Present", width: 90, type: "number" },
  { field: "lateDays", headerName: "Late", width: 70, type: "number" },
  { field: "halfDays", headerName: "Half Day", width: 90, type: "number" },
  { field: "absentDays", headerName: "Absent", width: 80, type: "number" },
  {
    field: "totalHours",
    headerName: "Total Hours",
    width: 110,
    renderCell: (p) => (
      <Typography sx={{ fontSize: "0.82rem" }}>
        {p.value != null ? `${p.value.toFixed(1)}h` : "—"}
      </Typography>
    ),
  },
  {
    field: "attendancePercentage",
    headerName: "Attendance %",
    width: 160,
    renderCell: (p) => <AttPct value={p.value} />,
  },
];

function SummaryStats({ data }) {
  if (!data?.employees?.length) return null;
  const emps = data.employees;
  const total = emps.length;
  const avgPct = total
    ? Math.round(emps.reduce((s, e) => s + (e.attendancePercentage || 0), 0) / total)
    : 0;
  const fullPresent = emps.filter((e) => e.attendancePercentage >= 90).length;
  const atRisk = emps.filter((e) => e.attendancePercentage < 75).length;

  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2}>
      {[
        { label: "Total Employees", value: total, color: "#418BFF" },
        { label: "Avg Attendance", value: `${avgPct}%`, color: avgPct >= 90 ? "#16a34a" : "#ea580c" },
        { label: "≥90% Attendance", value: fullPresent, color: "#16a34a" },
        { label: "At Risk (<75%)", value: atRisk, color: "#dc2626" },
      ].map((stat) => (
        <Box
          key={stat.label}
          sx={{
            flex: 1,
            bgcolor: "var(--ax-surface)",
            border: "1px solid var(--ax-border)",
            borderRadius: "var(--ax-radius-lg)",
            p: 2,
          }}
        >
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--ax-ink-2)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            {stat.label}
          </Typography>
          <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: stat.color, mt: 0.5 }}>
            {stat.value}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

function Attendance() {
  const [tabIndex, setTabIndex] = useState(0);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [dailyData, setDailyData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const period = PERIOD_TABS[tabIndex]?.value || "daily";

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: Cookies.get("jwtToken") } }),
    []
  );

  const fetchDaily = useCallback(() => {
    setLoading(true);
    axios
      .get(`${attendanceAdminDailyApi}?date=${date}`, authHeaders())
      .then((res) => setDailyData(res.data))
      .catch(() => toast.error("Failed to load daily attendance"))
      .finally(() => setLoading(false));
  }, [date, authHeaders]);

  const fetchSummary = useCallback(() => {
    setLoading(true);
    axios
      .get(`${attendanceAdminSummaryApi}?period=${period}`, authHeaders())
      .then((res) => setSummaryData(res.data))
      .catch(() => toast.error("Failed to load attendance summary"))
      .finally(() => setLoading(false));
  }, [period, authHeaders]);

  useEffect(() => {
    if (period === "daily") fetchDaily();
    else fetchSummary();
  }, [period, fetchDaily, fetchSummary]);

  const rows = period === "daily"
    ? (dailyData?.records || []).map((r, i) => ({ id: i, ...r }))
    : (summaryData?.employees || []).map((r, i) => ({ id: i, ...r }));

  const filtered = rows.filter((r) => {
    const q = search.toLowerCase();
    return (
      !q ||
      r.empId?.toLowerCase().includes(q) ||
      r.empName?.toLowerCase().includes(q) ||
      r.department?.toLowerCase().includes(q)
    );
  });

  const periodLabel = period === "daily"
    ? `${new Date(date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}`
    : `${summaryData?.startDate || ""} → ${summaryData?.endDate || ""}`;

  return (
    <Box p={{ xs: 2, sm: 3 }}>
      {/* Attendance Widget (for logged-in employee) */}
      <Box mb={3} maxWidth={480}>
        <AttendanceWidget />
      </Box>

      {/* Admin Attendance Panel */}
      <Box
        sx={{
          bgcolor: "var(--ax-surface)",
          border: "1px solid var(--ax-border)",
          borderRadius: "var(--ax-radius-lg)",
          overflow: "hidden",
        }}
      >
        {/* Title + Tabs */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          px={3}
          pt={2.5}
          pb={0}
          gap={1}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: "var(--ax-font-heading)",
                fontWeight: 500,
                fontSize: "1.1rem",
                letterSpacing: "-0.02em",
                color: "var(--ax-ink)",
              }}
            >
              Attendance Overview
            </Typography>
            {periodLabel && (
              <Typography sx={{ fontSize: "0.78rem", color: "var(--ax-ink-2)", mt: 0.25 }}>
                {periodLabel}
              </Typography>
            )}
          </Box>
        </Stack>

        <Tabs
          value={tabIndex}
          onChange={(_, v) => { setTabIndex(v); setSearch(""); }}
          sx={{
            px: 3,
            borderBottom: "1px solid var(--ax-border)",
            minHeight: 40,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.82rem",
              color: "var(--ax-ink-2)",
              minHeight: 40,
              py: 0,
            },
            "& .Mui-selected": { color: "var(--ax-blue) !important", fontWeight: 600 },
            "& .MuiTabs-indicator": { bgcolor: "var(--ax-blue)" },
          }}
        >
          {PERIOD_TABS.map((t) => (
            <Tab key={t.value} label={t.label} />
          ))}
        </Tabs>

        <Box p={2.5}>
          {/* Controls */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mb={2} alignItems="center">
            {period === "daily" && (
              <TextField
                type="date"
                size="small"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                inputProps={{ max: new Date().toISOString().slice(0, 10) }}
                sx={{ width: 180 }}
                label="Date"
                InputLabelProps={{ shrink: true }}
              />
            )}
            <TextField
              size="small"
              placeholder="Search employee..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ flex: 1, maxWidth: 280 }}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={period === "daily" ? fetchDaily : fetchSummary}
              disabled={loading}
              sx={{
                textTransform: "none",
                fontSize: "0.8rem",
                borderColor: "var(--ax-border)",
                color: "var(--ax-ink-2)",
                "&:hover": { borderColor: "var(--ax-blue)", color: "var(--ax-blue)" },
              }}
            >
              Refresh
            </Button>

            {period !== "daily" && summaryData && (
              <Chip
                label={`${summaryData.totalEmployees} employees · ${summaryData.workingDays} working days`}
                size="small"
                sx={{ bgcolor: "var(--ax-bg)", color: "var(--ax-ink-2)", fontSize: "0.74rem" }}
              />
            )}
          </Stack>

          {/* Summary stats for non-daily views */}
          {period !== "daily" && summaryData && <SummaryStats data={summaryData} />}

          {/* DataGrid */}
          {loading ? (
            <Stack alignItems="center" py={6}>
              <CircularProgress size={28} sx={{ color: "var(--ax-blue)" }} />
              <Typography sx={{ mt: 1.5, fontSize: "0.84rem", color: "var(--ax-ink-2)" }}>
                Loading attendance data...
              </Typography>
            </Stack>
          ) : (
            <ThemeProvider theme={theme}>
              <DataGrid
                rows={filtered}
                columns={period === "daily" ? dailyColumns : summaryColumns}
                pageSizeOptions={[25, 50, 100]}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                autoHeight
                disableRowSelectionOnClick
                sx={{
                  border: "1px solid var(--ax-border)",
                  borderRadius: "var(--ax-radius-lg)",
                  "& .MuiDataGrid-cell": { fontSize: "0.82rem" },
                  "& .MuiDataGrid-columnHeader": {
                    fontSize: "0.76rem",
                    fontWeight: 600,
                    color: "var(--ax-ink-2)",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  },
                }}
                slots={{
                  noRowsOverlay: () => (
                    <Stack alignItems="center" justifyContent="center" height="100%" py={4}>
                      <Typography sx={{ fontSize: "0.84rem", color: "var(--ax-ink-2)" }}>
                        No attendance data for this period
                      </Typography>
                    </Stack>
                  ),
                }}
              />
            </ThemeProvider>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default Attendance;
